import { field, type SearchDoc } from './guideSearch'
import { characterClasses } from '../data/guide/classes'
import { recipes, recipePath, recipeIngredients, recipeResults } from '../data/guide/recipes'
import { npcs } from '../data/guide/npcs'
import { thirdEvolution } from '../data/guide/evolutions'
import { npcLocationText, resolveNpcLocation } from './npcLocation'
import type { Npc } from '../types/progression'

/** Documentos del buscador para evoluciones, misiones, recetas y NPC. Se construyen una sola vez. */
export function buildProgressionDocs(): SearchDoc[] {
  const docs: SearchDoc[] = []

  docs.push({
    key: 'guia-evoluciones',
    groupKey: 'guia-evoluciones',
    group: 'Guías',
    title: 'Evoluciones de personaje',
    typeLabel: 'Guía interactiva',
    context: 'Elige tu clase y sigue su recorrido',
    path: '/guia/evoluciones',
    fields: [
      field('Evoluciones de personaje', 10, 'Título'),
      ...['evolucion', 'evoluciones', 'evolucionar personaje', 'cambio de clase', 'evo', 'segunda clase', 'tercera clase', 'quest', 'misiones de clase'].map((a) => field(a, 9, 'Nombre alternativo')),
      field('Segunda clase con Sebina, misión de Marlon, combo y tercera evolución por Barracks y Refuge.', 4, 'Descripción'),
    ],
  })

  characterClasses.forEach((c) => {
    const chain = [c.base, c.second, c.third].filter(Boolean).join(' → ')
    docs.push({
      key: `clase-${c.id}`,
      groupKey: `clase-${c.id}`,
      group: 'Guías',
      title: chain,
      typeLabel: 'Evolución por clase',
      context: c.second ? 'Sebina → tercera evolución' : 'Sin misión de Sebina',
      path: `/guia/evoluciones?clase=${c.id}`,
      entity: { type: 'class', id: c.id },
      fields: [
        field(chain, 10, 'Clase'),
        ...[...c.baseAliases, ...c.secondAliases, ...c.thirdAliases].map((a) => field(a, 9, 'Clase')),
        field(`evolucion ${c.base} evolucionar ${c.baseAliases.join(' ')}`, 5, 'Evolución'),
      ],
    })
  })

  const mission = (key: string, title: string, path: string, aliases: string[], text: string) =>
    docs.push({
      key,
      groupKey: key,
      group: 'Misiones',
      title,
      typeLabel: 'Misión',
      context: 'Evoluciones de personaje',
      path,
      fields: [field(title, 10, 'Título'), ...aliases.map((a) => field(a, 9, 'Nombre alternativo')), field(text, 4, 'Descripción')],
    })

  mission(
    'mision-sebina',
    'Segunda clase con Sebina',
    '/guia/evoluciones#sebina',
    ['quest sebina', 'mision sebina', 'segunda clase', 'evo 2 personaje', 'scroll of emperor', 'librito', 'broken sword', 'soul of wizard', 'tear of elf', 'abyssal eye', 'espada rota', 'alma del mago', 'lagrima de elfa', 'ojo abisal', 'pergamino del emperador'],
    'Sebina the Priest. Primera parte desde nivel 150 (10.000 Zen) y segunda parte (20.000 Zen) con el objeto de tu clase: Broken Sword, Soul Shard of Wizard, Tear of Elf o Abyssal Eye.',
  )
  mission(
    'mision-marlon',
    'Misión de Marlon',
    '/guia/evoluciones#marlon',
    ['quest marlon', 'mision marlon', 'quest del marlon', 'ring of honor', 'ring of glory', 'anillo de honor', 'anillo de marlon', 'puntos por nivel'],
    'Marlon: desde nivel 220 con la segunda clase y la segunda parte de Sebina terminada, 30.000 Zen y 1 Ring of Honor.',
  )
  mission(
    'mision-combo',
    'Desbloquear el combo (Blade Knight)',
    '/guia/evoluciones?clase=dk#combo',
    ['combo', 'combo bk', 'combo blade knight', 'desbloquear combo', 'desbloqueo combo', 'dark stone', 'piedra oscura'],
    'Parte adicional de Marlon solo para Blade Knight: desde nivel 220 con la primera parte de Marlon terminada, 20.000 Zen y 1 Dark Stone.',
  )
  mission(
    'mision-tercera',
    'Tercera evolución: Barracks y Refuge',
    '/guia/evoluciones#tercera',
    ['tercera evolucion', 'evo 3 personaje', 'tercera clase', 'quest 3', 'barracks', 'refuge', 'devin', 'apostle devin', 'quarel', 'gatekeeper', 'balram', 'death spirit', 'soram', 'dark elf', 'como entro a barracks', 'acceso barracks'],
    `${thirdEvolution.stage1.materials.map((m) => m.gameName).join(', ')} (1 de cada, nivel 380, 50.000 Zen). Barracks: 20 Balram, 20 Death Spirit y 20 Soram (nivel 400, 70.000 Zen). Refuge: 1 Dark Elf (nivel 400, 100.000 Zen).`,
  )

  docs.push({
    key: 'guia-alas',
    groupKey: 'guia-alas',
    group: 'Guías',
    title: 'Alas y capas',
    typeLabel: 'Guía interactiva',
    context: 'Arma Chaos · Evo 1 · Evo 2 · Capas · Condor Feather · Evo 3',
    path: '/guia/alas',
    fields: [
      field('Alas y capas', 10, 'Título'),
      ...['alas', 'wings', 'como hago las alas', 'crear alas', 'capas', 'chaos machine', 'combinaciones', 'recetas'].map((a) => field(a, 9, 'Nombre alternativo')),
      field('Recetas de la Chaos Machine para crear armas Chaos, alas de primera, segunda y tercera generación y capas.', 4, 'Descripción'),
    ],
  })

  docs.push({
    key: 'guia-combinaciones',
    groupKey: 'guia-combinaciones',
    group: 'Guías',
    title: 'Mascotas y combinaciones',
    typeLabel: 'Guía interactiva',
    context: 'Dark Horse · Dark Raven · Dinorant · Fenrir · entradas de eventos',
    path: '/guia/combinaciones',
    fields: [
      field('Mascotas y combinaciones', 10, 'Título'),
      ...['mascotas', 'monturas', 'pets', 'mascota', 'montura', 'combinaciones', 'pet trainer', 'entradas de eventos', 'tickets'].map((a) => field(a, 9, 'Nombre alternativo')),
      field('Cómo crear mascotas, monturas y entradas de eventos con el Trainer y la Chaos Machine.', 4, 'Descripción'),
    ],
  })

  const VERBS = ['crear', 'hacer', 'fabricar', 'combinar', 'como crear', 'como hacer', 'receta']
  recipes.forEach((r) => {
    const ingredients = recipeIngredients(r)
    const results = recipeResults(r)
    const names = Array.from(new Set([r.shortTitle, ...results.map((x) => x.gameName)]))
    docs.push({
      key: `receta-${r.id}`,
      groupKey: `receta-${r.id}`,
      group: 'Recetas',
      title: r.title,
      typeLabel: `Receta · ${r.machine ?? 'Chaos Machine'}`,
      context: r.summary,
      path: recipePath(r.id),
      // Imagen de la receta: la de su primer resultado identificado por item_id.
      entity: (() => {
        const id = results.find((x) => x.itemId)?.itemId
        return id ? { type: 'item' as const, id } : undefined
      })(),
      fields: [
        field(r.title, 10, 'Título'),
        field(r.shortTitle, 10, 'Título'),
        ...r.aliases.map((a) => field(a, 9, 'Nombre alternativo')),
        ...names.flatMap((nm) => VERBS.map((v) => field(`${v} ${nm}`, 8, 'Nombre alternativo'))),
        field(ingredients.map((m) => m.gameName ?? m.esName).join(', '), 4, 'Ingredientes'),
        field(ingredients.map((m) => m.esName).filter(Boolean).join(', '), 3, 'Ingredientes (traducción)'),
        field(results.map((x) => x.gameName).join(', '), 4, 'Resultados'),
      ],
    })
  })

  // Antes de cargar los archivos: ubicación de administración o de referencia. Al cargar, buildWorldDocs
  // sustituye estos documentos (misma clave) por la ubicación resuelta con los datos.
  npcs.forEach((n) => {
    const loc = resolveNpcLocation(n, { status: 'idle' })
    const text = loc.state === 'admin' ? npcLocationText(loc) : loc.state === 'loading' && loc.reference ? npcLocationText({ state: 'reference', reference: loc.reference }) : ''
    docs.push(npcDoc(n, text))
  })

  return docs
}

export function npcDoc(n: Npc, locationText: string): SearchDoc {
  return {
    key: `npc-${n.id}`,
    groupKey: `npc-${n.id}`,
    group: 'NPC',
    title: n.name,
    typeLabel: 'NPC',
    context: locationText ? `${n.role} · ${locationText}` : n.role,
    path: n.sectionPath,
    entity: n.monsterId !== undefined ? { type: 'monster', id: n.monsterId } : undefined,
    fields: [
      field(n.name, 10, 'NPC'),
      ...n.aliases.map((a) => field(a, 9, 'Nombre alternativo')),
      field(`${n.role}. Ubicación donde está el NPC. ${locationText}`, 4, 'Ubicación'),
    ],
  }
}
